import {
  FormFieldWidget,
  FormM2MTableFieldWidget,
  http,
  ModelFieldType,
  SPI,
  ViewType,
  Widget,
  ValidatorInfo,
  ValidatorStatus,
  isValidatorSuccess,
  WidgetSubjection
} from '@kunlun/dependencies';
import AfterSalesRefundsReturn from './AfterSalesRefundsReturn.vue';
import { OioMessage, OioNotification } from '@kunlun/vue-ui-antd';
import { cloneDeep } from 'lodash-es';
import XLSX from 'xlsx';
import { stringifyToGraphql as commonStringifyToGraphql } from '../../../../utils/serialize';

@SPI.ClassFactory(
  FormFieldWidget.Token({
    viewType: ViewType.Form,
    ttype: ModelFieldType.ManyToMany,
    widget: 'Table',
    name: 'returnDetailProxySaleSkus',
    model: [
      'deli.aries.seller.DeliAriesSellerTradeReverseOrderEdit',
      'deli.aries.seller.DeliAriesSellerTradeReverseOrderCreate'
    ],
    multi: true
  })
)
export class FormM2mMultiAfterSalesRefundsReturnFieldWidget extends FormM2MTableFieldWidget {
  public initialize(props) {
    super.initialize(props);
    this.setComponent(AfterSalesRefundsReturn);
    return this;
  }

  @Widget.Reactive()
  private get _instance() {
    return this.getSelf();
  }
  
  
  @Widget.Reactive()
  @Widget.BehaviorSubContext(Symbol('deleteRow'), {
    data: ''
  })
  protected deleteEvent !: WidgetSubjection<{
    data: string
  }>;

  private getGqlQueryName() {
    const map = {
      'deli.aries.seller.DeliAriesSellerTradeReverseOrderCreate': 'deliAriesSellerTradeReverseOrderCreateQuery',
      'deli.aries.seller.DeliAriesSellerTradeReverseOrderEdit': 'deliAriesSellerTradeReverseOrderEditQuery'
    } as const;
    const name = map[this.field.model];
    if (!name) throw new Error('name not found');
    return name;
  }

  private stringifyToGraphql(obj: any, enumPropNameList: string[] = []) {
    let json = JSON.stringify(obj);
    // 去除枚举属性值的引号
    if (enumPropNameList.length > 0) {
      const reg = new RegExp(`"(${enumPropNameList.join('|')})":"(.*?)"`, 'g');
      json = json.replace(reg, '"$1":$2');
    }
    // 去除所有属性名的引号
    const reg2 = new RegExp(`((?!\\\\).)"([^,]*?)((?!\\\\).)":`, 'g');

    return json.replace(reg2, '$1$2$3:');
  }

  private iterateObjDeep(source: Record<any, any> | any[], fn: (obj: Record<any, any>) => any) {
    if (Array.isArray(source)) {
      source.forEach((item) => {
        if (Object.prototype.toString.call(item) === '[object Object]') {
          fn(item);
          Object.values(source).forEach((item2) => {
            this.iterateObjDeep(item2, fn);
          });
        }
      });
    } else if (Object.prototype.toString.call(source) === '[object Object]') {
      fn(source);
      Object.values(source).forEach((item) => {
        this.iterateObjDeep(item, fn);
      });
    }
  }
  //import refund item
  @Widget.Method()
  async importReturnItems(itemDetailStr: string) {
    if (!this.formData.sellerId || !this.formData.buyerId || !this.formData.reverseType || !this.formData.salesOfficeId) {
      OioMessage.error('请将买卖家填写完整!');
      return;
    }

    const data: Record<any, any> = {
      detailProxySaleSkus: cloneDeep(this.formData.detailProxySaleSkus as any[]).filter((item) => item.outCode),
      itemDetailStr,
      buyerRelationApplyProxy: cloneDeep(this.formData.buyerRelationApplyProxy as any),
      reverseType: this.formData.reverseType,
      sellerId: this.formData.sellerId,
      buyerId: this.formData.buyerId,
      salesOfficeId: this.formData.salesOfficeId,
      reverseDisChannel: cloneDeep(this.formData.reverseDisChannel as any)
    };
    this.iterateObjDeep(data, (obj: Record<any, any>) => {
      delete obj.__draftId;
      delete obj._X_ROW_KEY;
    });

    this.iterateObjDeep([data], (obj: Record<any, any>) => {
      if (typeof obj.reverseDiscount === 'number') {
        obj.reverseDiscount = String(obj.reverseDiscount);
      }
      delete obj.__draftId;
      delete obj._X_ROW_KEY;
    });

    const gql = `
        query {
          ${this.getGqlQueryName()} {
            importReturnItems(
              data: ${commonStringifyToGraphql(data, ['reverseType', 'priceType'])}

            ) {
              reminds
              errMessages
              refundAmount
              refundType
              
              sourceType
              returnDetailProxySaleSkus {
              assemblyItemDisplay
                id
                skuId
                reverseDetailId
                sourcingTmsOrderDetailId
                expLabel
                outCode
                itemName
                itemCode
                itemNumber
                characteristics
                priceType
                externalMaterialGroup
                saleUnitName
                saleUnitPrice
                actualUnitPrice
                settlementPrice
                rebatePayPrice
                applyRefundUnitPrice
                reverseDiscount
                applyReverseNum
                applyRefundAmount
                deliAriesItemSpecificationsProxyList {
                  id
                  pcsCount
                  count
                  unitName
                  unitCode
                  conversionRules
                  isEnabled
                }
                reason {
                  id
                  code
                  reason
                }
                tradeOrderCode
                tmsOrderCode
                exWarehouseNum
                residueReturnNum
                isSecondTrace
              }
            }
          }
        }
      `;
    const res = await http.query(this.model.moduleName, gql);
    const resData: any = res.data?.[this.getGqlQueryName()]?.importReturnItems ?? {};

    if ((resData.reminds as string[])?.length) {
      OioNotification.warning('提示', (resData.reminds as string[]).join('\n'));
    }

    return resData;
  }

  @Widget.Method()
  async updateOne(row: any) {
    if (!this.formData.sellerId || !this.formData.buyerId || !this.formData.reverseType || !this.formData.salesOfficeId) {
      OioMessage.error('请将买卖家填写完整!');
      return;
    }
    if (!this.formData.reverseDisChannel) {
      OioMessage.error('请将分销渠道填写完整!');
      return;
    }

    const create: Record<any, any> = {
      returnDetailProxySaleSkus: cloneDeep(this.formData.returnDetailProxySaleSkus as any[]).filter((item) => item.outCode),
      buyerRelationApplyProxy: cloneDeep(this.formData.buyerRelationApplyProxy as any),
      reverseType: this.formData.reverseType,
      sellerId: this.formData.sellerId,
      buyerId: this.formData.buyerId,
      salesOfficeId: this.formData.salesOfficeId,
      reverseDisChannel: cloneDeep(this.formData.reverseDisChannel as any)
    };

    const copyRow = cloneDeep(row);
    // 不传row表示进行删除动作
    if (row) {
      if (!Object.values(copyRow).some((item) => item !== null && item !== undefined)) {
        return;
      }

      const index = (create.returnDetailProxySaleSkus as any[]).findIndex((i) => i.outCode === copyRow.outCode);
      if (index !== -1) {
        create.returnDetailProxySaleSkus.splice(index, 1);
      }
    }

    this.iterateObjDeep([create, copyRow], (obj: Record<any, any>) => {
      if (typeof obj.reverseDiscount === 'number') {
        obj.reverseDiscount = String(obj.reverseDiscount);
      }
      delete obj.__draftId;
      delete obj._X_ROW_KEY;
    });

    const gql = `
    query {
      ${this.getGqlQueryName()} {
        itemDetailConstructMirrorByReturnMoneyGoodsDetail(
          create: ${this.stringifyToGraphql(create, ['reverseType', 'priceType'])}
          data: ${this.stringifyToGraphql(copyRow ?? {}, ['priceType'])}
        ) {
          errMessages
          refundAmount
          returnDetailProxySaleSkus {
            id
            skuId
            reverseDetailId
            sourcingTmsOrderDetailId
            expLabel
            outCode
            itemName
            itemCode
            itemNumber
            characteristics
            priceType
            externalMaterialGroup
            saleUnitName
            saleUnitPrice
            actualUnitPrice
            applyRefundUnitPrice
            reverseDiscount
            applyReverseNum
            applyRefundAmount
            reason {
              id
              code
              reason
            }
            tradeOrderCode
            tmsOrderCode
            exWarehouseNum
            residueReturnNum
            isSecondTrace
          }
        }
      }
    }
    `;
    const res = await http.query(this.model.moduleName, gql);
    const resData = res.data[this.getGqlQueryName()].itemDetailConstructMirrorByReturnMoneyGoodsDetail;
    resData?.refundAmount && (this.formData.refundAmount = resData.refundAmount);
    return resData;
  }

  @Widget.Reactive()
  public setFormData(val) {
    this.change(val);
  }

  @Widget.Reactive()
  public _formData: Record<any, any> = {};

  @Widget.Watch('formData', { deep: true, immediate: true })
  public watchFormData(val) {
    this._formData = val;
  }

  @Widget.Reactive()
  private mountedProcessFlag = false;

  protected mounted() {
    super.mounted();
    if (this.mountedCallChaining) {
      this.mountedCallChaining.hook(this.path, async () => {
        await this.mountedProcess();
      });
    } else {
      this.mountedProcess();
    }
  }

  protected async mountedProcess() {
    this.mountedProcessFlag = true;
  }

  public async validator(): Promise<ValidatorInfo> {
    const res = await this.validatorSpecific(this.value);

    if (!isValidatorSuccess(res)) {
      return res;
    }

    this.formData.returnDetailProxySaleSkus = (this.formData.returnDetailProxySaleSkus as any[])?.filter((item) => item.outCode) ?? [];

    return {
      message: '',
      status: ValidatorStatus.Success,
      path: ''
    };
  }
}
